import * as React from "react";
import { Bar, BarChart as RechartsBarChart, XAxis, YAxis } from "recharts";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/Base/Chart";

interface BarChartProps {
  data: Array<{ [key: string]: string | number }>;
  index: string;
  categories: string[];
  colors?: string[];
  yAxisWidth?: number;
  barSize?: number;
  stacked?: boolean;
  className?: string;
}

/**
 *
 * @param root0
 * @param root0.data
 * @param root0.index
 * @param root0.categories
 * @param root0.colors
 * @param root0.yAxisWidth
 * @param root0.stacked
 * @param root0.className
 */
export function BarChart({
  data,
  index,
  categories,
  colors = ["hsl(var(--chart-1))"],
  yAxisWidth = 48,
  stacked = false,
  className,
}: BarChartProps) {
  const config = React.useMemo(() => {
    const config: { [key: string]: { label?: string; color?: string } } = {};

    categories.forEach((category, i) => {
      config[category] = {
        label: category.charAt(0).toUpperCase() + category.slice(1),
        color: colors[i % colors.length],
      };
    });

    return config;
  }, [categories, colors]);

  return (
    <ChartContainer config={config} className={className}>
      <RechartsBarChart
        data={data}
        margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
        barGap={0}
        barCategoryGap="5%"
        layout="horizontal"
        width={undefined}
        height={undefined}
      >
        <XAxis
          dataKey={index}
          stroke="#888888"
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => value}
        />
        <YAxis
          stroke="#888888"
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => `${value}`}
          width={yAxisWidth}
        />
        <ChartTooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <ChartTooltipContent
                  active={active}
                  payload={payload}
                  label={payload[0]?.payload[index]}
                />
              );
            }
            return null;
          }}
        />
        {categories.map((category, i) => (
          <Bar
            key={category}
            dataKey={category}
            fill={colors[i % colors.length]}
            radius={[4, 4, 0, 0]}
            stackId={stacked ? "stack" : undefined}
          />
        ))}
      </RechartsBarChart>
    </ChartContainer>
  );
}
